import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import CategorySubcategoryPicker from "@/components/CategorySubcategoryPicker";
import ImageUploader from "@/components/ImageUploader";
import VideoManager from "@/components/VideoManager";
import ProMiniSiteCatalog from "@/pages/pro/ProMiniSiteCatalog";
import ProMiniSitePages from "@/pages/pro/ProMiniSitePages";
import ProMiniSiteMessages from "@/pages/pro/ProMiniSiteMessages";
import { toast } from "sonner";
import { Sparkles, Plus, Gauge, Check, Eye, Store, Package, FileText, Inbox, Globe, Palette } from "lucide-react";

const Req = () => <span className="text-red-500" aria-hidden="true"> *</span>;

const BLANK = { name: "", description: "", category_id: "", subcategory_id: "", address: "", postal_code: "", city: "",
  legal_name: "", phone: "", email: "", website: "", google_review_url: "", logo: "", cover_image: "", opening_hours: "", coverage: "local", gallery: [], videos: [],
  tagline: "", theme_color: "", slug: "", seo_title: "", seo_description: "", siret: "" };
const SNAP_KEYS = ["name","description","category_id","subcategory_id","address","postal_code","city","legal_name","phone","email","website","google_review_url","logo","cover_image","opening_hours","coverage","gallery","videos","tagline","theme_color","slug","seo_title","seo_description","siret"];
const serialize = (x) => JSON.stringify(SNAP_KEYS.map((k) => x?.[k] ?? (Array.isArray(BLANK[k]) ? [] : "")));

function InfosTab({ b, setB, bizId, onSaved }) {
  const set = (k) => (e) => setB((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const [saved, setSaved] = useState({ logo: b.logo || "", cover_image: b.cover_image || "" });
  const [savedSnapshot, setSavedSnapshot] = useState(serialize(b));
  const dirty = serialize(b) !== savedSnapshot;
  const [kw, setKw] = useState({ list: [], input: "" });
  const [genKw, setGenKw] = useState(false);
  const [genDesc, setGenDesc] = useState(false);
  const [sirenLoading, setSirenLoading] = useState(false);
  const verifySiren = async () => {
    const q = (b.siret || "").replace(/\D/g, "");
    if (q.length !== 9 && q.length !== 14) { toast.error("Saisissez un SIREN (9 chiffres) ou un SIRET (14 chiffres)."); return; }
    setSirenLoading(true);
    try {
      const { data } = await api.get("/siren/lookup", { params: { q } });
      setB((s) => ({
        ...s,
        legal_name: data.legal_name || s.legal_name,
        address: data.address || s.address,
        postal_code: data.postal_code || s.postal_code,
        city: data.city || s.city,
        name: (s.name || "").trim() ? s.name : (data.enseigne || data.legal_name || s.name),
        siret: data.siret || data.siren || s.siret,
      }));
      toast.success(`Entreprise trouvée : ${data.enseigne || data.legal_name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vérification impossible."); }
    finally { setSirenLoading(false); }
  };

  useEffect(() => {
    setSaved({ logo: b.logo || "", cover_image: b.cover_image || "" });
    setSavedSnapshot(serialize(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizId]);

  const selectedKeywords = () => kw.list.filter((k) => k.on).map((k) => k.text);

  const seo = useMemo(() => {
    const checks = [
      { ok: !!(b.name || "").trim(), w: 10, tip: "Ajoutez le nom de l'établissement" },
      { ok: !!b.category_id, w: 10, tip: "Choisissez une catégorie principale" },
      { ok: !!b.subcategory_id, w: 5, tip: "Précisez une sous-catégorie" },
      { ok: !!(b.city || "").trim(), w: 8, tip: "Indiquez votre ville" },
      { ok: (b.description || "").trim().length >= 120, w: 22, tip: "Rédigez une description d'au moins 120 caractères (idéalement générée avec vos mots-clés)" },
      { ok: !!(b.email || "").trim(), w: 5, tip: "Ajoutez un e-mail de contact" },
      { ok: !!(b.logo || b.cover_image || (b.gallery || []).length), w: 15, tip: "Ajoutez au moins une image" },
      { ok: !!(b.logo && b.cover_image), w: 5, tip: "Ajoutez un logo ET une photo de couverture" },
      { ok: !!(b.phone || "").trim(), w: 5, tip: "Ajoutez un numéro de téléphone" },
      { ok: !!(b.website || "").trim(), w: 5, tip: "Ajoutez l'adresse de votre site web" },
      { ok: !!(b.google_review_url || "").trim(), w: 5, tip: "Ajoutez votre lien d'avis Google" },
      { ok: !!(b.opening_hours || "").trim(), w: 5, tip: "Renseignez vos horaires d'ouverture" },
    ];
    const score = checks.reduce((s, c) => s + (c.ok ? c.w : 0), 0);
    const tips = checks.filter((c) => !c.ok).map((c) => c.tip);
    let label = "À compléter", color = "#EF4444";
    if (score >= 85) { label = "Excellent"; color = "#16A34A"; }
    else if (score >= 65) { label = "Bon"; color = "#65A30D"; }
    else if (score >= 40) { label = "Correct"; color = "#F59E0B"; }
    return { score, tips, label, color };
  }, [b]);

  const gp = useMemo(() => {
    const origin = (typeof window !== "undefined" && window.location?.origin) || "https://proxireduc-28.fr";
    const name = (b.name || "").trim() || "Votre établissement";
    const city = (b.city || "").trim();
    const title = (b.seo_title || "").trim() || `${name}${city ? ` à ${city}` : ""} — Mini-site local | PROXIreduc-28`;
    const crumb = `${origin.replace(/^https?:\/\//, "")} › commerce › ${name.toLowerCase().replace(/\s+/g, "-")}`;
    const d = (b.seo_description || b.description || "").trim();
    const desc = d
      ? (d.slice(0, 160) + (d.length > 160 ? "…" : ""))
      : `${name}${city ? ` à ${city}` : ""} en Eure-et-Loir : produits, services, horaires et coordonnées. Découvrez ce mini-site local sur PROXIreduc-28.`;
    return { title, crumb, desc };
  }, [b]);

  const hasImage = !!(b.logo || b.cover_image || (b.gallery || []).length);
  const missing = () => {
    const m = [];
    if (!b.name?.trim()) m.push("nom");
    if (!b.category_id) m.push("catégorie principale");
    if (!b.city?.trim()) m.push("ville");
    if (!b.description?.trim()) m.push("description");
    if (!b.email?.trim()) m.push("email");
    if (!hasImage) m.push("au moins une image");
    return m;
  };

  const save = async () => {
    const m = missing();
    if (m.length) { toast.error(`Mini-site incomplet. Éléments obligatoires manquants : ${m.join(", ")}.`); return; }
    try {
      const { data } = await api.post("/pro/business", b);
      setSaved({ logo: b.logo || "", cover_image: b.cover_image || "" });
      setSavedSnapshot(serialize(b));
      toast.success("Mini-site enregistré — votre mini-site est complet !");
      if (data?.business?.id) onSaved(data.business.id);
    }
    catch { toast.error("Erreur"); }
  };

  const generateKeywords = async () => {
    setGenKw(true);
    try {
      const { data } = await api.post("/pro/ai/description-keywords", { name: b.name, city: b.city, category_id: b.category_id });
      const list = (data.keywords || []).map((t) => ({ text: t, on: true }));
      setKw((k) => ({ ...k, list }));
      if (!list.length) toast.info("Aucun mot-clé généré, réessayez.");
      else toast.success(`${list.length} mots-clés générés et pré-sélectionnés — décochez ceux que vous ne voulez pas, puis générez la description.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération IA indisponible."); }
    finally { setGenKw(false); }
  };

  const toggleKw = (i) => setKw((k) => ({ ...k, list: k.list.map((x, j) => (j === i ? { ...x, on: !x.on } : x)) }));
  const addKw = () => { const t = kw.input.trim(); if (!t) return; if (kw.list.some((x) => x.text.toLowerCase() === t.toLowerCase())) { setKw((k) => ({ ...k, input: "" })); return; } setKw((k) => ({ ...k, list: [...k.list, { text: t, on: true }], input: "" })); };

  const generateDescription = async () => {
    setGenDesc(true);
    try {
      const { data } = await api.post("/pro/ai/description", { name: b.name, city: b.city, category_id: b.category_id, keywords: selectedKeywords() });
      if (data.description) { setB((s) => ({ ...s, description: data.description })); toast.success("Description générée en intégrant vos mots-clés — adaptez-la à votre image."); }
      else toast.info("Aucune description générée, réessayez.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération IA indisponible."); }
    finally { setGenDesc(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-3xl">
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid="seo-score">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Gauge size={16} className="text-[#C84B31]"/> Référencement Google</div>
          <div className="text-sm font-bold" style={{ color: seo.color }} data-testid="seo-score-value">{seo.score}/100 · {seo.label}</div>
        </div>
        <div className="mt-2 h-2.5 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${seo.score}%`, background: seo.color }} />
        </div>
        {seo.tips.length > 0 ? (
          <details className="mt-2">
            <summary className="text-xs text-slate-500 cursor-pointer select-none">Conseils pour améliorer votre visibilité ({seo.tips.length})</summary>
            <ul className="mt-1.5 text-xs text-slate-600 list-disc pl-5 space-y-0.5" data-testid="seo-tips">
              {seo.tips.slice(0, 6).map((t, i) => (<li key={i}>{t}</li>))}
            </ul>
          </details>
        ) : (
          <p className="mt-2 text-xs text-emerald-700 font-medium">Mini-site parfaitement optimisé pour Google.</p>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="google-preview">
        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Aperçu dans Google</div>
        <div className="max-w-xl">
          <div className="text-xs text-slate-600 truncate" style={{ fontFamily: "arial,sans-serif" }}>{gp.crumb}</div>
          <div className="text-[#1a0dab] text-lg leading-snug truncate" style={{ fontFamily: "arial,sans-serif" }}>{gp.title}</div>
          <div className="text-sm text-slate-600 mt-0.5 leading-snug" style={{ fontFamily: "arial,sans-serif" }}>{gp.desc}</div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Simulation indicative : plus votre mini-site est complet et riche en mots-clés, mieux il ressort dans Google.</p>
      </div>
      <div><Label>Nom<Req /></Label><Input value={b.name} onChange={set("name")} data-testid="biz-name"/></div>

      <CategorySubcategoryPicker
        value={{ category_id: b.category_id, subcategory_id: b.subcategory_id }}
        onChange={({ category_id, subcategory_id }) =>
          setB((s) => ({ ...s, category_id, subcategory_id }))}
        testIdPrefix="biz"
        required
      />

      <div><Label>Ville<Req /></Label><Input value={b.city} onChange={set("city")} data-testid="biz-city"/></div>

      <div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Label>Description<Req /></Label>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={generateKeywords} disabled={genKw}
                    className="h-8 text-[#C84B31] border-orange-200 hover:bg-orange-50" data-testid="biz-ai-keywords">
              <Sparkles size={13} className={`mr-1 ${genKw ? "animate-pulse" : ""}`}/> {genKw ? "Génération…" : "1. 20 mots-clés (IA)"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={generateDescription} disabled={genDesc}
                    className="h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="biz-ai-description">
              <Sparkles size={13} className={`mr-1 ${genDesc ? "animate-pulse" : ""}`}/> {genDesc ? "Rédaction…" : "2. Générer la description (IA)"}
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed bg-orange-50/60 border border-orange-100 rounded-lg px-3 py-2" data-testid="biz-ai-help">
          <span className="font-semibold text-slate-700">Astuce référencement :</span> si vous le souhaitez, générez d'abord les <strong>mots-clés</strong> (ils reprennent votre activité, votre ville et le département). Ils sont tous <strong>pré-sélectionnés</strong> : décochez ceux que vous ne voulez pas, ajoutez les vôtres, puis cliquez sur <strong>Générer la description</strong> : elle sera rédigée en intégrant ces mots-clés pour un <strong>référencement optimal sur Google</strong>.
        </p>
        {kw.list.length > 0 && (
          <div className="mt-2 mb-2 rounded-lg border border-orange-100 bg-orange-50/40 p-3" data-testid="biz-keywords">
            <p className="text-[11px] text-slate-500 mb-1.5">Mots-clés qui seront intégrés à la description — cliquez pour (dé)sélectionner, ou ajoutez les vôtres :</p>
            <div className="flex flex-wrap gap-1.5">
              {kw.list.map((k, i) => (
                <button key={i} type="button" onClick={() => toggleKw(i)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${k.on ? "bg-[#C84B31] text-white border-[#C84B31]" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}
                  data-testid={`biz-kw-${i}`}>{k.text}</button>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Input value={kw.input} onChange={(e) => setKw((k) => ({ ...k, input: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }} placeholder="Ajouter un mot-clé…" className="max-w-xs h-8" data-testid="biz-kw-input" />
              <Button type="button" variant="outline" size="sm" onClick={addKw} data-testid="biz-kw-add"><Plus size={14} className="mr-1" /> Ajouter</Button>
              <span className="text-xs text-slate-400">{kw.list.filter((k) => k.on).length} sélectionné(s)</span>
            </div>
          </div>
        )}
        <Textarea rows={4} value={b.description} onChange={set("description")} data-testid="biz-desc"/>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><Label>Adresse</Label><Input value={b.address} onChange={set("address")}/></div>
        <div><Label>Code postal</Label><Input value={b.postal_code} onChange={set("postal_code")}/></div>
        <div><Label>Téléphone</Label><Input value={b.phone} onChange={set("phone")}/></div>
        <div><Label>Email<Req /></Label><Input value={b.email} onChange={set("email")} data-testid="biz-email"/></div>
        <div className="col-span-2"><Label>Site web</Label><Input value={b.website} onChange={set("website")}/></div>
        <div className="col-span-2">
          <Label>SIREN / SIRET</Label>
          <div className="flex gap-2">
            <Input value={b.siret} onChange={set("siret")} placeholder="Ex. 123 456 789 00012" data-testid="biz-siret"/>
            <Button type="button" variant="outline" className="shrink-0" onClick={verifySiren} disabled={sirenLoading} data-testid="biz-siret-verify">
              {sirenLoading ? "Vérification…" : "Vérifier"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Saisissez votre SIREN (9 chiffres) ou SIRET (14 chiffres) puis « Vérifier » pour remplir automatiquement la raison sociale et l'adresse.</p>
        </div>
        <div className="col-span-2"><Label>Raison sociale</Label><Input value={b.legal_name} onChange={set("legal_name")} placeholder="Nom juridique officiel de l'entreprise" data-testid="biz-legal-name"/></div>
        <div className="col-span-2">
          <Label>Lien avis Google</Label>
          <Input value={b.google_review_url} onChange={set("google_review_url")} placeholder="https://g.page/r/… ou lien « Rédiger un avis »" data-testid="biz-google-review-url"/>
          <p className="text-[11px] text-slate-500 mt-1">Collez votre lien « Rédiger un avis » Google. Il alimente le présentoir QR et le bouton « Laisser un avis Google » sur votre mini-site. (Mini-site local référencé google SEO → Avis → Obtenir plus d'avis → Copier le lien.)</p>
        </div>
      </div>
      <div><Label>Horaires</Label><Input value={b.opening_hours} onChange={set("opening_hours")}/></div>

      {/* Personnalisation du mini-site */}
      <div className="pt-3 border-t border-slate-100 space-y-3" data-testid="minisite-customize">
        <div className="flex items-center gap-2"><Palette size={16} className="text-[#C84B31]" /><h3 className="font-heading font-bold text-base">Personnalisation du mini-site</h3></div>
        <div><Label>Accroche (slogan affiché en page d'accueil)</Label><Input value={b.tagline} onChange={set("tagline")} placeholder="Ex. Artisan boulanger depuis 1998 à Chartres" data-testid="biz-tagline"/></div>
        <div className="flex items-center gap-3">
          <div>
            <Label>Couleur d'accent</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={b.theme_color || "#C84B31"} onChange={set("theme_color")} className="h-9 w-12 rounded-lg border border-slate-200 cursor-pointer" data-testid="biz-theme-color"/>
              <Input value={b.theme_color} onChange={set("theme_color")} placeholder="#C84B31" className="w-32 h-9"/>
              {b.theme_color && <button type="button" onClick={() => setB((s) => ({ ...s, theme_color: "" }))} className="text-xs text-slate-400 hover:text-slate-600">Réinitialiser</button>}
            </div>
          </div>
        </div>
        <div><Label>Titre SEO personnalisé (facultatif)</Label><Input value={b.seo_title} onChange={set("seo_title")} placeholder="Laissez vide pour un titre automatique optimisé" data-testid="biz-seo-title"/></div>
        <div><Label>Description SEO personnalisée (facultatif)</Label><Textarea rows={2} value={b.seo_description} onChange={set("seo_description")} placeholder="Résumé affiché dans Google (max ~160 caractères)" data-testid="biz-seo-desc"/></div>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <Label>Logo</Label>
        <p className="text-xs text-slate-500 mb-2">Téléchargez votre logo (JPG, PNG ou WEBP).</p>
        {saved.logo && (
          <div className="mb-3 flex items-center gap-3" data-testid="biz-logo-current">
            <img src={saved.logo} alt="Logo actuel" className="h-16 w-16 rounded-xl object-cover border border-slate-200 bg-white" />
            <span className="text-xs text-slate-500">Logo actuellement en ligne</span>
          </div>
        )}
        <ImageUploader value={b.logo ? [b.logo] : []} max={1} testId="image-uploader-logo"
                       onChange={(arr) => setB((s) => ({ ...s, logo: arr[0] || "" }))} />
      </div>
      <div>
        <Label>Image de couverture</Label>
        <p className="text-xs text-slate-500 mb-2">Téléchargez une image de couverture mise en avant sur votre mini-site (JPG, PNG ou WEBP).</p>
        {saved.cover_image && (
          <div className="mb-3" data-testid="biz-cover-current">
            <img src={saved.cover_image} alt="Couverture actuelle" className="h-28 w-full max-w-sm rounded-xl object-cover border border-slate-200 bg-white" />
            <span className="block mt-1 text-xs text-slate-500">Couverture actuellement en ligne</span>
          </div>
        )}
        <ImageUploader value={b.cover_image ? [b.cover_image] : []} max={1} testId="image-uploader-cover"
                       onChange={(arr) => setB((s) => ({ ...s, cover_image: arr[0] || "" }))} />
      </div>
      <div className="pt-2 border-t border-slate-100">
        <Label>Galerie photos de votre vitrine<Req /></Label>
        <p className="text-xs text-slate-500 mb-2">Au moins une image est obligatoire (logo, couverture ou galerie). Montrez votre boutique, vos produits, votre équipe… jusqu'à 6 photos (JPG, PNG ou WEBP). La 1ʳᵉ sert d'image mise en avant.</p>
        <ImageUploader value={b.gallery || []} max={6} testId="image-uploader-gallery"
                       onChange={(arr) => setB((s) => ({ ...s, gallery: arr }))} />
      </div>
      <div className="pt-2 border-t border-slate-100">
        <Label>Vidéos de présentation</Label>
        <p className="text-xs text-slate-500 mb-2">Ajoutez une visite, une présentation ou un reportage : collez un lien YouTube/Vimeo ou téléversez un fichier vidéo.</p>
        <VideoManager value={b.videos || []} max={4}
                      onChange={(arr) => setB((s) => ({ ...s, videos: arr }))} />
      </div>
      <div>
        <Label>Zone d'intervention</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <button type="button" onClick={()=>setB((s)=>({...s, coverage: "local"}))}
                  className={`p-3 rounded-xl border text-left text-sm ${b.coverage==="local"?"border-[#C84B31] bg-orange-50":"border-slate-200 bg-white"}`}>
            <div className="font-semibold">Ma ville / mon pôle</div>
          </button>
          <button type="button" onClick={()=>setB((s)=>({...s, coverage: "department"}))}
                  className={`p-3 rounded-xl border text-left text-sm ${b.coverage==="department"?"border-[#C84B31] bg-orange-50":"border-slate-200 bg-white"}`}>
            <div className="font-semibold">Tout l'Eure-et-Loir</div>
          </button>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={!dirty}
                className={dirty ? "btn-shine text-white" : "bg-emerald-600 text-white hover:bg-emerald-600 cursor-default disabled:opacity-100"}
                data-testid="biz-save-btn">
          {dirty ? "Enregistrer" : <><Check size={16} className="mr-1.5"/> Enregistré</>}
        </Button>
        {bizId && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vitrine/${bizId}`} target="_blank" rel="noopener noreferrer" data-testid="biz-preview-btn">
            <Button type="button" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
              <Eye size={16} className="mr-1.5"/> Aperçu du mini-site
            </Button>
          </a>
        )}
        {dirty
          ? <span className="text-sm text-amber-600 font-medium" data-testid="biz-save-status">Modifications non enregistrées</span>
          : (bizId && <span className="text-sm text-emerald-700 font-medium" data-testid="biz-save-status">Mini-site à jour</span>)}
      </div>
    </div>
  );
}

const TABS = [
  { key: "infos", label: "Informations", icon: Store },
  { key: "catalog", label: "Produits & Services", icon: Package },
  { key: "pages", label: "Pages perso", icon: FileText },
  { key: "messages", label: "Messages reçus", icon: Inbox },
];

export default function ProBusiness() {
  const [b, setB] = useState({ ...BLANK });
  const [bizId, setBizId] = useState("");
  const [tab, setTab] = useState("infos");

  useEffect(() => {
    api.get("/pro/business").then((r) => {
      if (r.data.business) {
        const merged = { ...BLANK, ...r.data.business };
        setB(merged);
        setBizId(merged.id || "");
      }
    });
  }, []);

  const needsBiz = !bizId && tab !== "infos";

  return (
    <div data-testid="pro-business">
      <h1 className="font-heading text-3xl font-bold">Mon mini-site</h1>
      <div className="mt-3 max-w-2xl rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="minisite-seo-help">
        <div className="font-heading font-bold text-slate-900 flex items-center gap-2"><Globe size={17} className="text-emerald-700"/> Complétez votre mini-site pour être trouvé sur Google</div>
        <p className="mt-1.5 text-sm text-slate-700">
          Plus votre mini-site est complet (logo, photos, description, horaires, mots-clés), mieux vous ressortez sur Google et les moteurs de recherche (ex. « boulangerie Chartres »). C'est votre véritable page vitrine sur PROXIreduc-28, et elle vous offre un <b>lien supplémentaire</b> qui renforce votre fiche Google et votre site internet.
        </p>
      </div>

      <div className="mt-5 inline-flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1" data-testid="pro-business-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`pro-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === t.key ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
        {bizId && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vitrine/${bizId}`} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
             data-testid="pro-view-minisite-top">
            <Globe size={15} /> Voir mon mini-site
          </a>
        )}
      </div>

      <div className="mt-6">
        {tab === "infos" && <InfosTab b={b} setB={setB} bizId={bizId} onSaved={setBizId} />}
        {tab !== "infos" && needsBiz && (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500 max-w-2xl" data-testid="needs-biz">
            Créez d'abord votre mini-site dans l'onglet <strong>Informations</strong> (enregistrez vos infos de base), puis revenez ici.
          </div>
        )}
        {tab === "catalog" && !needsBiz && <ProMiniSiteCatalog />}
        {tab === "pages" && !needsBiz && <ProMiniSitePages bizId={bizId} />}
        {tab === "messages" && !needsBiz && <ProMiniSiteMessages />}
      </div>
    </div>
  );
}
